import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { compressImageIfNeeded, readFileAsBase64 } from '../lib/imageCompression'
import AddCategoryInline from './AddCategoryInline'
import AddPaymentMethodInline from './AddPaymentMethodInline'

const teal = '#0D9488'
const tealDark = '#0F766E'
const plum = '#1E1040'

/**
 * ReceiptUploadModal
 * Props:
 *   initialMode: 'scan' | 'upload' | 'manual' | null — which upload path to start on
 *   defaultTagId: uuid — which tag to default to (usually matches the active tab on Receipts page)
 *   tags: array — all the user's tags (schedule_c, company_card, reimbursable, personal)
 *   categories: array — user's categories
 *   paymentMethods: array — user's saved payment methods
 *   onClose: () => void
 *   onSaved: (transaction) => void — fired after successful save
 */
export default function ReceiptUploadModal({
  initialMode = null,
  defaultTagId = null,
  tags = [],
  categories: propCategories = [],
  paymentMethods: propPaymentMethods = [],
  onClose,
  onSaved,
}) {
  const { t } = useTranslation()

  // Human-readable explanation of each tag type — shown as hint under tag picker
  const tagHint = (tag) => {
    if (!tag) return ''
    return t(`receipt_modal.tag_hint.${tag.tag_type}`, { defaultValue: '' })
  }

  const [file, setFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [fileType, setFileType] = useState(null) // 'image' | 'pdf'
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState('')
  const [showReviewForm, setShowReviewForm] = useState(initialMode === 'manual')

  // Tag — the 'bucket' the receipt flows into
  const [tagId, setTagId] = useState(defaultTagId || (tags[0]?.id || ''))

  const [merchant, setMerchant] = useState('')
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])
  const [categoryId, setCategoryId] = useState('')
  const [paymentMethodId, setPaymentMethodId] = useState('')
  const [memo, setMemo] = useState('')

  const [categories, setCategories] = useState(propCategories)
  // Sync internal state when parent finishes loading categories (mobile race fix)
  useEffect(() => {
    if (propCategories && propCategories.length > 0) {
      setCategories(propCategories)
    }
  }, [propCategories])
  const [paymentMethods, setPaymentMethods] = useState(propPaymentMethods)
  // Sync internal state when parent finishes loading payment methods (same pattern as categories)
  useEffect(() => {
    if (propPaymentMethods && propPaymentMethods.length > 0) {
      setPaymentMethods(propPaymentMethods)
    }
  }, [propPaymentMethods])
  const [saving, setSaving] = useState(false)
  const [showAddCategory, setShowAddCategory] = useState(false)
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false)

  const cameraInputRef = useRef(null)
  const anyFileInputRef = useRef(null)

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [onClose])

  // Auto-open correct file picker if launched with initialMode (from Dashboard quick-actions)
  useEffect(() => {
    if (initialMode === 'scan') {
      setTimeout(() => cameraInputRef.current?.click(), 100)
    } else if (initialMode === 'upload') {
      setTimeout(() => anyFileInputRef.current?.click(), 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When user picks a payment method, auto-switch tag to its default (if it has one)
  useEffect(() => {
    if (!paymentMethodId) return
    const pm = paymentMethods.find(p => p.id === paymentMethodId)
    if (pm?.default_tag_id && pm.default_tag_id !== tagId) {
      setTagId(pm.default_tag_id)
      // also clear category since new tag may have different relevant categories
      setCategoryId('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethodId])

  const detectFileType = (mimeType) => {
    if (mimeType === 'application/pdf') return 'pdf'
    if (mimeType.startsWith('image/')) return 'image'
    return null
  }

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    setError('')

    const detectedType = detectFileType(selectedFile.type)
    if (!detectedType) {
      setError(t('receipt_modal.error_invalid_file'))
      return
    }

    setFileType(detectedType)
    setScanning(true)

    try {
      const processedFile = await compressImageIfNeeded(selectedFile)
      setFile(processedFile)

      const url = URL.createObjectURL(processedFile)
      setPreviewUrl(url)

      const { base64, mediaType } = await readFileAsBase64(processedFile)

      const { data: { session } } = await supabase.auth.getSession()

      const { data: fnResult, error: fnError } = await supabase.functions.invoke(
        'scan-receipt',
        {
          body: { base64Data: base64, mediaType },
          headers: session ? { Authorization: `Bearer ${session.access_token}` } : {},
        }
      )

      if (fnError) throw new Error(fnError.message)

      if (fnResult?.success && fnResult?.data) {
        const extracted = fnResult.data
        if (extracted.merchant) setMerchant(extracted.merchant)
        if (extracted.amount != null) setAmount(String(extracted.amount))
        if (extracted.date) setDate(extracted.date)
        // AI might return "Visa 1234" — try to match a saved payment method by last 4
        if (extracted.payment_method) {
          const extractedLower = extracted.payment_method.toLowerCase()
          const matchedPm = paymentMethods.find(pm => {
            if (!pm.last_4) return false
            return extractedLower.includes(pm.last_4)
          })
          if (matchedPm) setPaymentMethodId(matchedPm.id)
        }
        if (extracted.notes) setMemo(extracted.notes)

        if (extracted.category_hint) {
          const matched = categories.find(c => c.name.toLowerCase() === extracted.category_hint.toLowerCase())
          if (matched) setCategoryId(matched.id)
        }
      } else if (fnResult?.error) {
        setError(t('receipt_modal.error_scan_partial', { detail: fnResult.error }))
      }
    } catch (err) {
      console.error('Scan error:', err)
      setError(t('receipt_modal.error_scan_failed'))
    } finally {
      setScanning(false)
      setShowReviewForm(true)
    }
  }

  const handleSave = async () => {
    // Tag is required — guard here too so a receipt can never be saved with tag_id = NULL
    if (!tagId) {
      setError(t('receipt_modal.error_no_tag'))
      return
    }
    if (!merchant || !amount) {
      setError(t('receipt_modal.error_required_fields'))
      return
    }

    setSaving(true)
    setError('')

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error(t('receipt_modal.error_not_signed_in'))

      let receiptUrl = null
      let receiptFilePath = null
      let receiptFileType = null
      let receiptMimeType = null

      if (file) {
        const ext = file.name.split('.').pop()?.toLowerCase() || (fileType === 'pdf' ? 'pdf' : 'jpg')
        const storagePath = `${user.id}/${Date.now()}.${ext}`

        const { error: uploadErr } = await supabase.storage
          .from('receipts')
          .upload(storagePath, file, { upsert: false, contentType: file.type })

        if (uploadErr) throw uploadErr

        const { data: urlData } = supabase.storage.from('receipts').getPublicUrl(storagePath)
        receiptUrl = urlData?.publicUrl || null
        receiptFilePath = storagePath
        receiptFileType = fileType
        receiptMimeType = file.type
      }

      const selectedTag = tags.find(tt => tt.id === tagId)
      const payload = {
        user_id: user.id,
        transaction_date: date,
        merchant,
        amount: parseFloat(amount),
        category_id: categoryId || null,
        payment_method_id: paymentMethodId || null,
        tag_id: tagId || null,
        memo: memo || null,
        // Keep is_business for backward compat — derive from tag_type
        is_business: selectedTag?.tag_type !== 'personal',
        receipt_url: receiptUrl,
        receipt_file_path: receiptFilePath,
        receipt_file_type: receiptFileType,
        receipt_mime_type: receiptMimeType,
        ai_extracted: !!(file && !error),
      }

      const { data: savedRow, error: insertErr } = await supabase
        .from('transactions')
        .insert(payload)
        .select()
        .single()

      if (insertErr) throw insertErr

      onSaved?.(savedRow)
      onClose()
    } catch (err) {
      console.error('Save error:', err)
      setError(err.message || t('receipt_modal.error_save_failed'))
    } finally {
      setSaving(false)
    }
  }

  const reset = () => {
    setFile(null)
    setFileType(null)
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(null)
    setMerchant('')
    setAmount('')
    setDate(new Date().toISOString().split('T')[0])
    setCategoryId('')
    setPaymentMethodId('')
    setMemo('')
    setError('')
    setShowReviewForm(false)
  }

  // View decision — dead simple:
  //   showPicker: no file yet, and review form not open → show the 3 options
  //   showPreview: file exists (whether still scanning or done)
  //   showReviewForm: scanning finished OR manual mode
  const showPicker = !file && !showReviewForm

  // Save is blocked until a tag is picked and the required fields are filled
  const saveDisabled = !tagId || !merchant || !amount || saving

  return (
    <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.title}>
            {showReviewForm ? t('receipt_modal.review_title') : t('receipt_modal.add_title')}
          </div>
          <button style={styles.closeBtn} onClick={onClose} aria-label={t('common.close')}>✕</button>
        </div>

        {showPicker && (
          <>
            {/* Tag picker — FIRST thing shown so user picks where receipt belongs */}
            {tags.length > 0 && (
              <div style={styles.tagPickerBox}>
                <div style={styles.tagPickerLabel}>{t('receipt_modal.tag_picker_label')}</div>
                <select
                  style={styles.input}
                  value={tagId}
                  onChange={e => setTagId(e.target.value)}
                >
                  {tags.map(tag => (
                    <option key={tag.id} value={tag.id}>{tag.icon} {tag.name}</option>
                  ))}
                </select>
                <div style={styles.hint}>
                  {tagHint(tags.find(tt => tt.id === tagId))}
                </div>
              </div>
            )}

            <p style={styles.pickerHint}>{t('receipt_modal.picker_prompt')}</p>
            <div style={styles.pickerGrid}>
              <button style={styles.pickerCard} onClick={() => cameraInputRef.current?.click()}>
                <span style={styles.pickerIcon}>📷</span>
                <span style={styles.pickerText}>
                  <span style={styles.pickerTitle}>{t('receipt_modal.option_scan_title')}</span>
                  <span style={styles.pickerSub}>{t('receipt_modal.option_scan_sub')}</span>
                </span>
              </button>
              <button style={styles.pickerCard} onClick={() => anyFileInputRef.current?.click()}>
                <span style={styles.pickerIcon}>📤</span>
                <span style={styles.pickerText}>
                  <span style={styles.pickerTitle}>{t('receipt_modal.option_upload_title')}</span>
                  <span style={styles.pickerSub}>{t('receipt_modal.option_upload_sub')}</span>
                </span>
              </button>
              <button style={styles.pickerCard} onClick={() => setShowReviewForm(true)}>
                <span style={styles.pickerIcon}>✏️</span>
                <span style={styles.pickerText}>
                  <span style={styles.pickerTitle}>{t('receipt_modal.option_manual_title')}</span>
                  <span style={styles.pickerSub}>{t('receipt_modal.option_manual_sub')}</span>
                </span>
              </button>
            </div>
          </>
        )}

        {previewUrl && fileType === 'image' && (
          <div style={styles.previewArea}>
            <img src={previewUrl} alt={t('receipt_modal.preview_alt')} style={styles.previewImg} />
            {scanning && (
              <div style={styles.scanningOverlay}>
                <div style={styles.scanningText}>🔍 {t('receipt_modal.scanning_title')}</div>
                <div style={styles.scanningSub}>{t('receipt_modal.scanning_sub')}</div>
              </div>
            )}
          </div>
        )}

        {previewUrl && fileType === 'pdf' && (
          <div style={styles.pdfPreview}>
            <div style={styles.pdfIcon}>📄</div>
            <div>
              <div style={styles.pdfName}>{file?.name || t('receipt_modal.pdf_default_name')}</div>
              <div style={styles.pdfSub}>
                {file ? `${(file.size / 1024).toFixed(0)} KB · PDF` : 'PDF'}
              </div>
            </div>
            {scanning && (
              <div style={styles.pdfScanning}>
                <div style={styles.scanningText}>{t('receipt_modal.scanning_pdf')}</div>
              </div>
            )}
          </div>
        )}

        {showReviewForm && !scanning && (
          <div style={styles.form}>
            {file && !error && (
              <div style={styles.aiNote}>
                <span>✨</span> {t('receipt_modal.ai_note')}
              </div>
            )}

            {/* Tag picker */}
            {tags.length > 0 && (
              <div style={styles.field}>
                <label style={styles.label}>{t('receipt_modal.tag_label')} *</label>
                <select
                  style={styles.input}
                  value={tagId}
                  onChange={e => { setTagId(e.target.value); setCategoryId(''); setShowAddCategory(false) }}
                >
                  {tags.map(tag => (
                    <option key={tag.id} value={tag.id}>{tag.icon} {tag.name}</option>
                  ))}
                </select>
                <div style={styles.hint}>
                  {tagHint(tags.find(tt => tt.id === tagId))}
                </div>
              </div>
            )}

            <div style={styles.field}>
              <label style={styles.label}>{t('receipt_modal.merchant_label')} *</label>
              <input
                type="text"
                style={styles.input}
                placeholder={t('receipt_modal.merchant_placeholder')}
                value={merchant}
                onChange={e => setMerchant(e.target.value)}
                autoFocus={!file}
              />
            </div>

            <div style={styles.fieldRow}>
              <div style={styles.field}>
                <label style={styles.label}>{t('receipt_modal.amount_label')} *</label>
                <input
                  type="number"
                  step="0.01"
                  style={styles.input}
                  placeholder="0.00"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{t('receipt_modal.date_label')} *</label>
                <input
                  type="date"
                  style={styles.input}
                  value={date}
                  onChange={e => setDate(e.target.value)}
                />
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>{t('receipt_modal.category_label')}</label>
              <select
                style={styles.input}
                value={categoryId}
                onChange={e => {
                  if (e.target.value === '__ADD_NEW__') {
                    setShowAddCategory(true)
                  } else {
                    setCategoryId(e.target.value)
                  }
                }}
              >
                <option value="">{t('receipt_modal.select_placeholder')}</option>
                {(() => {
                  // Determine suggestion order: personal tag suggests personal first, all others suggest business first
                  const selectedTag = tags.find(tt => tt.id === tagId)
                  const preferredType = selectedTag?.tag_type === 'personal' ? 'personal' : 'business'
                  const suggested = categories.filter(c => c.category_type === preferredType)
                  const others = categories.filter(c => c.category_type !== preferredType)
                  return (
                    <>
                      {suggested.length > 0 && (
                        <optgroup label={t('receipt_modal.optgroup_suggested')}>
                          {suggested.map(c => (
                            <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                          ))}
                        </optgroup>
                      )}
                      {others.length > 0 && (
                        <optgroup label={t('receipt_modal.optgroup_other')}>
                          {others.map(c => (
                            <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  )
                })()}
                <option value="__ADD_NEW__">➕ {t('receipt_modal.add_category_option')}</option>
              </select>
              {showAddCategory && (
                <AddCategoryInline
                  categoryType={(() => {
                    const tag = tags.find(tt => tt.id === tagId)
                    return tag?.tag_type === 'personal' ? 'personal' : 'business'
                  })()}
                  variant={(() => {
                    const tag = tags.find(tt => tt.id === tagId)
                    return tag?.tag_type === 'personal' ? 'personal' : 'business'
                  })()}
                  onAdded={(newCat) => {
                    setCategories(prev => [...prev, newCat])
                    setCategoryId(newCat.id)
                    setShowAddCategory(false)
                  }}
                  onCancel={() => setShowAddCategory(false)}
                />
              )}
            </div>

            <div style={styles.field}>
              <label style={styles.label}>{t('receipt_modal.payment_label')}</label>
              <select
                style={styles.input}
                value={paymentMethodId}
                onChange={e => {
                  if (e.target.value === '__ADD_NEW_PM__') {
                    setShowAddPaymentMethod(true)
                  } else {
                    setPaymentMethodId(e.target.value)
                  }
                }}
              >
                <option value="">{t('receipt_modal.select_payment_placeholder')}</option>
                {paymentMethods.map(pm => (
                  <option key={pm.id} value={pm.id}>
                    {pm.label}{pm.last_4 ? ` ••${pm.last_4}` : ''}
                  </option>
                ))}
                <option value="__ADD_NEW_PM__">➕ {t('receipt_modal.add_payment_option')}</option>
              </select>
              {showAddPaymentMethod && (
                <AddPaymentMethodInline
                  defaultTagId={tagId}
                  onAdded={(newPm) => {
                    setPaymentMethods(prev => [...prev, newPm])
                    setPaymentMethodId(newPm.id)
                    setShowAddPaymentMethod(false)
                  }}
                  onCancel={() => setShowAddPaymentMethod(false)}
                />
              )}
              {paymentMethods.length === 0 && !showAddPaymentMethod && (
                <div style={styles.hint}>
                  {t('receipt_modal.no_payment_hint')}
                </div>
              )}
            </div>

            <div style={styles.field}>
              <label style={styles.label}>{t('receipt_modal.memo_label')}</label>
              <input
                type="text"
                style={styles.input}
                placeholder={t('receipt_modal.memo_placeholder')}
                value={memo}
                onChange={e => setMemo(e.target.value)}
              />
            </div>
          </div>
        )}

        {error && <div style={styles.error}>{error}</div>}

        {showReviewForm && !scanning && (
          <div style={styles.actions}>
            <button style={styles.btnSecondary} onClick={reset}>
              {t('receipt_modal.start_over')}
            </button>
            <button
              style={{ ...styles.btnPrimary, ...(saveDisabled ? styles.btnPrimaryDisabled : {}) }}
              onClick={handleSave}
              disabled={saveDisabled}
            >
              {saving ? t('common.saving') : t('receipt_modal.save_btn')}
            </button>
          </div>
        )}

        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <input
          ref={anyFileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,image/webp,application/pdf"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </div>
    </div>
  )
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 2000,
    background: 'rgba(15, 23, 42, 0.75)',
    backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    background: '#fff',
    borderRadius: '20px',
    padding: '24px',
    width: '100%',
    maxWidth: '520px',
    maxHeight: '92vh',
    overflowY: 'auto',
    boxShadow: '0 32px 80px rgba(0,0,0,0.3)',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: plum,
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '16px',
  },
  title: {
    fontFamily: "'Fraunces', serif",
    fontSize: '22px',
    fontWeight: 800,
    color: plum,
    letterSpacing: '-0.3px',
  },
  closeBtn: {
    background: '#F3F4F6',
    border: 'none',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    fontSize: '14px',
    color: '#6B7280',
    cursor: 'pointer',
    fontWeight: 700,
  },
  pickerHint: {
    fontSize: '14px',
    color: '#6B7280',
    margin: '0 0 16px 0',
    fontWeight: 500,
  },
  pickerGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  pickerCard: {
    background: '#fff',
    border: '1.5px solid #E5E7EB',
    borderRadius: '14px',
    padding: '16px 18px',
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    width: '100%',
    transition: 'all 0.15s',
  },
  pickerIcon: {
    fontSize: '28px',
    flexShrink: 0,
    width: '42px',
    textAlign: 'center',
    display: 'inline-block',
  },
  pickerText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  pickerTitle: {
    fontSize: '15px',
    fontWeight: 700,
    color: plum,
    marginBottom: '3px',
  },
  pickerSub: {
    fontSize: '13px',
    color: '#6B7280',
    fontWeight: 500,
  },
  previewArea: {
    position: 'relative',
    marginBottom: '16px',
    borderRadius: '12px',
    overflow: 'hidden',
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
  },
  previewImg: {
    display: 'block',
    width: '100%',
    maxHeight: '260px',
    objectFit: 'contain',
    background: '#F9FAFB',
  },
  scanningOverlay: {
    position: 'absolute', inset: 0,
    background: 'rgba(13, 148, 136, 0.9)',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    gap: '8px',
  },
  scanningText: { fontSize: '17px', fontWeight: 700, color: '#fff', fontFamily: "'Fraunces', serif" },
  scanningSub: { fontSize: '13px', color: 'rgba(255,255,255,0.9)', fontWeight: 500 },
  pdfPreview: {
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '12px',
    padding: '16px 20px',
    marginBottom: '16px',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '14px',
    alignItems: 'center',
    position: 'relative',
  },
  pdfIcon: { fontSize: '36px' },
  pdfName: { fontSize: '14px', fontWeight: 700, color: plum, marginBottom: '2px' },
  pdfSub: { fontSize: '12px', color: '#6B7280' },
  pdfScanning: {
    position: 'absolute', inset: 0,
    background: 'rgba(13, 148, 136, 0.92)',
    borderRadius: '12px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  aiNote: {
    background: 'rgba(13, 148, 136, 0.08)',
    color: tealDark,
    padding: '8px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    marginBottom: '14px',
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },
  hint: {
    fontSize: '12px',
    color: '#9CA3AF',
    marginTop: '6px',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  tagPickerBox: {
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '12px',
    padding: '14px 16px',
    marginBottom: '18px',
  },
  tagPickerLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px',
  },
  form: { marginTop: '4px' },
  field: { marginBottom: '14px' },
  fieldRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 700,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    borderRadius: '10px',
    border: '1.5px solid #E5E7EB',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    color: plum,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    background: '#fff',
  },
  error: {
    background: '#FEE2E2',
    color: '#991B1B',
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginTop: '12px',
    fontWeight: 500,
  },
  actions: {
    display: 'flex',
    gap: '10px',
    marginTop: '20px',
  },
  btnSecondary: {
    flex: 1,
    padding: '13px',
    background: '#fff',
    color: plum,
    border: '1.5px solid #E5E7EB',
    borderRadius: '100px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnPrimary: {
    flex: 2,
    padding: '13px',
    background: teal,
    color: '#042f2e',
    border: 'none',
    borderRadius: '100px',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 4px 14px ${teal}40`,
  },
  btnPrimaryDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
}
